import React, { useState } from "react";
import { motion } from "framer-motion";
import { X, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { RelocationOrder, useCounterRelocationOrderMutation } from "../../../redux/services/relocationApi";
import InfoTooltip from "../../../components/Tooltip/InfoTooltip";
import { usePermission } from "../../../utils/permissions";

interface Props {
  request: RelocationOrder;
  onClose: () => void;
}

const formatPrice = (n: number) =>
  new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN", minimumFractionDigits: 0 }).format(n);

const CounterOfferModal: React.FC<Props> = ({ request, onClose }) => {
  const [price, setPrice] = useState("");
  const [notes, setNotes] = useState("");
  const [sendCounter, { isLoading }] = useCounterRelocationOrderMutation();
  const { can } = usePermission();
  const canCounter = can("send relocation counter offer");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = Number(price.replace(/,/g, ""));
    if (!parsed || parsed <= 0) {
      toast.error("Enter a valid price");
      return;
    }
    if (!notes.trim()) {
      toast.error("Please provide a reason for the counter offer");
      return;
    }
    try {
      await sendCounter({ id: request.id, offered_price: parsed, description: notes.trim() }).unwrap();
      toast.success("Counter offer sent to customer");
      onClose();
    } catch {
      toast.error("Failed to send counter offer");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.93, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.93, y: 20 }}
        transition={{ duration: 0.2 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
      >
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <MessageSquare size={18} className="text-[#143887]" />
            <h3 className="font-semibold text-[#00032A]">Send Counter Offer</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            type="button"
          >
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Request summary */}
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-xs text-gray-500">Customer</p>
            <p className="font-semibold text-sm text-[#00032A] mt-0.5">{request.customer?.name ?? "Unknown customer"}</p>
            <div className="mt-3 flex items-start justify-between gap-3">
              <div>
                <p className="text-xs text-gray-500">Their offer</p>
                <p className="text-xl font-bold text-red-500 mt-0.5">{formatPrice(parseFloat(request.price))}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500">Route</p>
                <p className="text-xs font-medium text-[#00032A] mt-0.5 max-w-[160px] text-right leading-relaxed">
                  {request.pickup_location} → {request.destination}
                </p>
              </div>
            </div>
          </div>

          {/* Counter price input */}
          <div>
            <label className="text-sm font-medium text-[#00032A] block mb-1.5">
              Your Counter Price <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 font-medium select-none">
                ₦
              </span>
              <input
                type="number"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="Enter your price"
                min={1}
                required
                className="w-full pl-8 pr-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-[#143887] focus:ring-1 focus:ring-[#143887] transition-colors"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="text-sm font-medium text-[#00032A] mb-1.5 flex items-center gap-1.5">
              Reason <span className="text-red-500">*</span>
              <InfoTooltip
                position="right"
                text="This note is shown directly to the customer alongside your counter price, so keep it clear and professional."
              />
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add a note to the customer..."
              rows={3}
              className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-[#143887] focus:ring-1 focus:ring-[#143887] resize-none transition-colors"
            />
          </div>

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 rounded-lg border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !canCounter}
              title={!canCounter ? "You don't have permission to send a counter offer." : undefined}
              className="flex-1 py-2.5 rounded-lg bg-[#143887] hover:bg-[#0f2d6b] text-white text-sm font-semibold transition-colors disabled:opacity-50"
            >
              {isLoading ? "Sending..." : "Send Counter Offer"}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default CounterOfferModal;
